import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Button, Divider, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  ClearOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import './EmailWysiwygEditor.css';

const ToolbarButton = ({ active, onClick, icon, title, disabled }) => (
  <Tooltip title={title}>
    <Button
      type={active ? 'primary' : 'text'}
      size="small"
      icon={icon}
      onClick={onClick}
      disabled={disabled}
    />
  </Tooltip>
);

/**
 * Rich-text composer for HTML email bodies, backed by TipTap. `value`/`onChange`
 * carry the HTML string — the same string already sent as `body_html` and shown
 * in the live email preview, so no extra plumbing is needed at the call site.
 */
const EmailWysiwygEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || 'Write your email…' }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    editorProps: {
      attributes: { class: 'wysiwyg-content' },
    },
  });

  // Sync external resets (e.g. "Reset" button, loading a draft) without fighting
  // the user's cursor on every keystroke — only when the editor is actually empty
  // or the incoming value is a full reset to empty.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value === current) return;
    if (!value || current === '<p></p>' || current === '') {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="wysiwyg-editor">
      <div className="wysiwyg-toolbar">
        <ToolbarButton title="Bold" icon={<BoldOutlined />} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton title="Italic" icon={<ItalicOutlined />} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton title="Underline" icon={<UnderlineOutlined />} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarButton title="Strikethrough" icon={<StrikethroughOutlined />} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
        <Divider type="vertical" />
        <Button
          type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'text'}
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </Button>
        <Button
          type={editor.isActive('heading', { level: 3 }) ? 'primary' : 'text'}
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </Button>
        <Divider type="vertical" />
        <ToolbarButton title="Bullet list" icon={<UnorderedListOutlined />} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton title="Numbered list" icon={<OrderedListOutlined />} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <Divider type="vertical" />
        <ToolbarButton title="Align left" icon={<AlignLeftOutlined />} active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
        <ToolbarButton title="Align center" icon={<AlignCenterOutlined />} active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
        <ToolbarButton title="Align right" icon={<AlignRightOutlined />} active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />
        <Divider type="vertical" />
        <ToolbarButton title="Link" icon={<LinkOutlined />} active={editor.isActive('link')} onClick={setLink} />
        <ToolbarButton title="Clear formatting" icon={<ClearOutlined />} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} />
        <Divider type="vertical" />
        <ToolbarButton title="Undo" icon={<UndoOutlined />} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} />
        <ToolbarButton title="Redo" icon={<RedoOutlined />} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default EmailWysiwygEditor;
